// ContractInfoForm.tsx

import React, { useMemo } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { PhoneCodeSelect } from './PhoneCode/PhoneCodeSelect';
import { ApplyFormData } from '../ApplyFormComponent1';
import { useLocation } from 'react-router-dom';

// ▼ 国名リストをインポート（前の例と同じ）
//   COUNTRY_LIST_EN / COUNTRY_LIST_JP を持つファイルを想定
import {
  COUNTRY_LIST_EN,
  COUNTRY_LIST_JP,
} from '@/components/common/countryLists';

const generateRange = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

/**
 * メールアドレス専用のカスタムバリデーション
 * 条件
 * 1. 特殊文字 (/、!、"、#、$、%) を含むものはNG
 * 2. @が2つ以上含まれるものはNG
 * 3. @の直前が英数字以外
 * 4. '.'または'_'が2つ以上連続しているものはNG
 * 5. 最初の文字が英数字以外のものはNG
 */
const validateCustomEmail = (
  value: string,
  useEnglish: boolean
): true | string => {
  const errorsList: string[] = [];

  // (1) 特殊文字 (/、!、"、#、$、%) を含むかどうか
  const forbiddenChars = /[!"#$%/]/;
  if (forbiddenChars.test(value)) {
    errorsList.push(
      useEnglish
        ? `Cannot contain these special characters (/!"#$%)`
        : `特殊文字 (/!"#$%) は使用できません`
    );
  }

  // (2) @ が2つ以上含まれる
  const atCount = (value.match(/@/g) || []).length;
  if (atCount >= 2) {
    errorsList.push(
      useEnglish
        ? `Cannot contain 2 or more '@' characters`
        : `@を2つ以上含むアドレスは無効です`
    );
  }

  // (3) @の直前が英数字以外
  const atIndex = value.indexOf('@');
  if (atIndex > 0) {
    const charBeforeAt = value[atIndex - 1];
    if (!/^[A-Za-z0-9]$/.test(charBeforeAt)) {
      errorsList.push(
        useEnglish
          ? `The character before '@' must be alphanumeric`
          : `@の直前は英数字である必要があります`
      );
    }
  }

  // (4) '.' または '_' が2つ以上連続
  if (/\.\.|__/.test(value)) {
    errorsList.push(
      useEnglish
        ? `Cannot have consecutive '..' or '__'`
        : `「..」または「__」を連続使用できません`
    );
  }

  // (5) 最初の文字が英数字以外
  if (!/^[A-Za-z0-9]/.test(value)) {
    errorsList.push(
      useEnglish
        ? `The first character must be alphanumeric`
        : `メールアドレスの最初の文字は英数字である必要があります`
    );
  }

  if (errorsList.length > 0) {
    return errorsList.join('\n'); // 改行区切りでまとめて返す
  }
  return true;
};

export const ContractInfoForm: React.FC = () => {
  // URLから言語判定 (例: /en/... なら英語、/ja/... なら日本語)
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<ApplyFormData>();

  // 生年月日の最新値を監視
  const birthYearValue = useWatch({ control, name: 'contractData.birthYear' });
  const birthMonthValue = useWatch({
    control,
    name: 'contractData.birthMonth',
  });
  const birthDayValue = useWatch({ control, name: 'contractData.birthDay' });

  // 年・月・日
  const years = useMemo(
    () => generateRange(1900, new Date().getFullYear()),
    []
  );
  const months = useMemo(() => generateRange(1, 12), []);
  const days = useMemo(() => generateRange(1, 31), []);

  // 国番号リスト (libphonenumber-js)
  const computedPhoneCodeList = useMemo(() => {
    const countries = getCountries();
    return countries.map((iso2) => {
      const callingCode = getCountryCallingCode(iso2);
      return {
        iso2,
        code: `+${callingCode}`,
      };
    });
  }, []);

  // デザイン用クラス
  const inputCls = `w-full border border-black mt-1 h-[27px] text-sm px-1 py-0`;

  // URL セグメントに応じて、使う国名リストを切り替える
  // const countryList = useEnglish ? COUNTRY_LIST_EN : COUNTRY_LIST_JP;
  // わかりやすさのため言語設定に関わらずenのリストを使う
  const countryList = COUNTRY_LIST_EN;

  return (
    <div className="w-full max-md:max-w-md max-md:mx-auto px-5 py-5 bg-[#f6f3e6] mt-5">
      {/* 姓・名 */}
      <div className="mb-5">
        <div className="flex space-x-4">
          {/* 姓 */}
          <div className="w-1/2">
            <label className="block text-gray-800 text-sm font-bold">
              {useEnglish ? 'Last Name' : '姓'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. Tohkyu' : '例）東急'}
                className={`${inputCls} ${
                  errors.contractData?.firstName ? 'border-red-500' : ''
                }`}
                {...register('contractData.firstName', {
                  required: useEnglish
                    ? 'Last name is required'
                    : '姓が未入力です',
                })}
              />
              {errors.contractData?.firstName && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.contractData?.firstName && (
              <div className="text-red-500 text-sm mt-1">
                {errors.contractData.firstName.message?.toString()}
              </div>
            )}
          </div>

          {/* 名 */}
          <div className="w-1/2">
            <label className="block text-gray-800 text-sm font-bold">
              {useEnglish ? 'First Name' : '名'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. Taro' : '例）太郎'}
                className={`${inputCls} ${
                  errors.contractData?.lastName ? 'border-red-500' : ''
                }`}
                {...register('contractData.lastName', {
                  required: useEnglish
                    ? 'First name is required'
                    : '名が未入力です',
                })}
              />
              {errors.contractData?.lastName && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.contractData?.lastName && (
              <div className="text-red-500 text-sm mt-1">
                {errors.contractData.lastName.message?.toString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* フリガナ（セイ・メイ） */}
      <div className="mb-5">
        <div className="flex space-x-4">
          {/* セイ */}
          <div className="w-1/2">
            <label className="block text-gray-800 text-sm font-bold">
              {useEnglish ? 'Furigana(Last)' : 'フリガナ（セイ）'}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. TOHKYU' : '例）トウキュウ'}
                className={`${inputCls} ${
                  errors.contractData?.firstNameKana ? 'border-red-500' : ''
                }`}
                {...register('contractData.firstNameKana', {
                  required: useEnglish
                    ? 'Furigana(Last) is required'
                    : 'フリガナ(姓)が未入力です',
                })}
              />
              {errors.contractData?.firstNameKana && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.contractData?.firstNameKana && (
              <div className="text-red-500 text-sm mt-1">
                {errors.contractData.firstNameKana.message?.toString()}
              </div>
            )}
          </div>

          {/* メイ */}
          <div className="w-1/2">
            <label className="block text-gray-800 text-sm font-bold">
              {useEnglish ? 'Furigana(First)' : 'フリガナ（メイ）'}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. TARO' : '例）タロウ'}
                className={`${inputCls} ${
                  errors.contractData?.lastNameKana ? 'border-red-500' : ''
                }`}
                {...register('contractData.lastNameKana', {
                  required: useEnglish
                    ? 'Furigana(First) is required'
                    : 'フリガナ(名)が未入力です',
                })}
              />
              {errors.contractData?.lastNameKana && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.contractData?.lastNameKana && (
              <div className="text-red-500 text-sm mt-1">
                {errors.contractData.lastNameKana.message?.toString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 国名 -> ドロップダウンに変更 (valueをlabelにして送信) */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          {useEnglish ? 'Country' : '国籍'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            className={`${inputCls} ${
              errors.contractData?.country ? 'border-red-500' : ''
            }`}
            {...register('contractData.country', {
              required: useEnglish ? 'Country is required' : '国名が未入力です',
            })}
          >
            <option value="">
              {useEnglish ? 'Select a country' : '国を選択してください'}
            </option>
            {countryList.map((country) => (
              // keyにはISOコード等を使用し、実際のvalueにはcountry.labelを持たせる
              <option key={country.value} value={country.label}>
                {country.label}
              </option>
            ))}
          </select>
          {errors.contractData?.country && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.contractData?.country && (
          <div className="text-red-500 text-sm mt-1">
            {errors.contractData.country.message?.toString()}
          </div>
        )}
      </div>

      {/* 住所 */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          {useEnglish ? 'Address' : '住所'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={
              useEnglish
                ? 'e.g. 31-2 Sakuragaokacho, Shibuya, Tokyo'
                : '例）東京都渋谷区桜丘町31-2'
            }
            className={`${inputCls} ${
              errors.contractData?.address ? 'border-red-500' : ''
            }`}
            {...register('contractData.address', {
              required: useEnglish ? 'Address is required' : '住所が未入力です',
            })}
          />
          {errors.contractData?.address && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.contractData?.address && (
          <div className="text-red-500 text-sm mt-1">
            {errors.contractData.address.message?.toString()}
          </div>
        )}
      </div>

      {/* 生年月日 */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          {useEnglish ? 'Birthdate' : '生年月日'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-2 mt-1">
          {/* 年 */}
          <div className="relative w-1/3">
            <select
              className={`${inputCls} ${
                errors.contractData?.birthYear ? 'border-red-500' : ''
              }`}
              {...register('contractData.birthYear', {
                required: useEnglish ? 'Year is required' : '生年が未入力です',
                validate: (value) => {
                  const year = parseInt(value, 10);
                  const month = parseInt(birthMonthValue || '0', 10);
                  const day = parseInt(birthDayValue || '0', 10);
                  if (!year || !month || !day) return true;
                  const birthDate = new Date(year, month - 1, day);
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();
                  if (
                    m < 0 ||
                    (m === 0 && today.getDate() < birthDate.getDate())
                  ) {
                    age--;
                  }
                  return (
                    age >= 18 ||
                    (useEnglish
                      ? 'You must be at least 18 years old'
                      : '未成年者のお申込みはできません')
                  );
                },
              })}
            >
              <option value="">{useEnglish ? 'Year' : '年'}</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {useEnglish ? y : `${y}年`}
                </option>
              ))}
            </select>
          </div>
          {/* 月 */}
          <div className="relative w-1/3">
            <select
              className={`${inputCls} ${
                errors.contractData?.birthMonth ? 'border-red-500' : ''
              }`}
              {...register('contractData.birthMonth', {
                required: useEnglish ? 'Month is required' : '月が未入力です',
              })}
            >
              <option value="">{useEnglish ? 'Month' : '月'}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {useEnglish ? m : `${m}月`}
                </option>
              ))}
            </select>
          </div>
          {/* 日 */}
          <div className="relative w-1/3">
            <select
              className={`${inputCls} ${
                errors.contractData?.birthDay ? 'border-red-500' : ''
              }`}
              {...register('contractData.birthDay', {
                required: useEnglish ? 'Day is required' : '日が未入力です',
                validate: (value) => {
                  const year = parseInt(birthYearValue || '0', 10);
                  const month = parseInt(birthMonthValue || '0', 10);
                  const day = parseInt(value, 10);
                  if (!year || !month || !day) return true;
                  const birthDate = new Date(year, month - 1, day);
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();
                  if (
                    m < 0 ||
                    (m === 0 && today.getDate() < birthDate.getDate())
                  ) {
                    age--;
                  }
                  return (
                    age >= 18 ||
                    (useEnglish
                      ? 'You must be at least 18 years old'
                      : '未成年者のお申込みはできません')
                  );
                },
              })}
            >
              <option value="">{useEnglish ? 'Day' : '日'}</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {useEnglish ? d : `${d}日`}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* 生年月日全体のエラー表示 */}
        {(errors.contractData?.birthYear ||
          errors.contractData?.birthMonth ||
          errors.contractData?.birthDay) && (
          <div className="text-red-500 text-sm mt-1">
            {errors.contractData.birthYear?.message?.toString() ||
              errors.contractData.birthMonth?.message?.toString() ||
              errors.contractData.birthDay?.message?.toString()}
          </div>
        )}
      </div>

      {/* 性別 */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          {useEnglish ? 'Gender' : '性別'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-3 mt-1">
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="male"
              className="mr-1"
              {...register('contractData.gender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別が未入力です',
              })}
            />
            {useEnglish ? 'Male' : '男性'}
          </label>
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="female"
              className="mr-1"
              {...register('contractData.gender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別が未入力です',
              })}
            />
            {useEnglish ? 'Female' : '女性'}
          </label>
        </div>
        {errors.contractData?.gender && (
          <div className="text-red-500 text-sm mt-1">
            {errors.contractData.gender.message?.toString()}
          </div>
        )}
      </div>

      {/* メールアドレス */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          {useEnglish ? 'Email Address' : 'メールアドレス'}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="email"
            placeholder={
              useEnglish ? 'Ex: sample@example.com' : '例: sample@example.com'
            }
            className={`${inputCls} ${
              errors.contractData?.email ? 'border-red-500' : ''
            }`}
            {...register('contractData.email', {
              required: useEnglish
                ? 'Email is required'
                : 'メールアドレスが未入力です',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: useEnglish
                  ? 'Invalid email format'
                  : 'メールアドレスの形式が正しくありません',
              },
              validate: (value) => validateCustomEmail(value, useEnglish),
            })}
          />
        </div>
        {errors.contractData?.email && (
          <div className="text-red-500 text-sm mt-1 whitespace-pre-line">
            {errors.contractData.email.message?.toString()}
          </div>
        )}
      </div>

      {/* 電話番号 (国番号 + 電話番号) */}
      <div className="mb-5">
        <label className="block text-gray-800 text-sm font-bold">
          電話番号 <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-2 mt-1">
          <div className="">
            <Controller
              name="contractData.phoneNumber"
              control={control}
              rules={{ required: '電話番号が未入力です' }}
              render={({ field }) => <PhoneCodeSelect {...field} />}
            />
            {errors.contractData?.phoneNumber && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
            )}
          </div>
        </div>
        {errors.contractData?.phoneNumber && (
          <div className="text-red-500 text-sm mt-1">
            {errors.contractData.phoneNumber.message?.toString()}
          </div>
        )}
      </div>
    </div>
  );
};
